import React, { useState, useEffect } from 'react';
import FormClientes from '../../../components/ui/Admin/ClientesComponents/FormClientes';
import ClientesCard from '../../../components/ui/Admin/ClientesComponents/ClientesCard';
import { fetchWithAuth } from '../../../js/authToken';
import API_BASE_URL from '../../../js/urlHelper';
import SkeletonClienteCard from '../../../components/ui/Admin/ClientesComponents/LoadingSkeletons/SkeletonLoadingClienteCard';

const ClientesUI = ({ apiBasePath = '/api/admin/clientes' }) => {
  const [clientes, setClientes] = useState([]);
  const [filteredClientes, setFilteredClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [avalFilter, setAvalFilter] = useState('all');
  const clientesPerPage = 6;

  // SCROLL: función para scrollear el contenedor interno
  const scrollToTopMainContainer = () => {
    const cont = document.getElementById('main-scroll-container');
    if (cont && cont.scrollHeight > cont.clientHeight) {
      cont.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    fetchClientes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    filterClientes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientes, searchTerm, statusFilter, avalFilter]);

  const fetchClientes = async () => {
    try {
      setLoading(true);
      const response = await fetchWithAuth(`${API_BASE_URL}${apiBasePath}/getclients`);

      if (!response.ok) {
        throw new Error('Error al cargar clientes');
      }

      const data = await response.json();
      setClientes(data.clientes);
      setError(null);
    } catch (err) {
      setError('No se pudieron cargar los clientes. Por favor, intente nuevamente más tarde.');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const filterClientes = () => {
    let result = [...clientes];

    if (statusFilter !== 'all') {
      result = result.filter(cliente => cliente.estado === parseInt(statusFilter));
    }

    if (avalFilter !== 'all') {
      const filterAval = avalFilter === 'true';
      result = result.filter(cliente => cliente.datos?.aval === filterAval);
    }

    if (searchTerm) {
      const termWords = searchTerm.toLowerCase().split(/\s+/);

      result = result.filter(cliente => {
        const nombre = cliente.datos?.nombre?.toLowerCase() || '';
        const apellidoPaterno = cliente.datos?.apellidoPaterno?.toLowerCase() || '';
        const apellidoMaterno = cliente.datos?.apellidoMaterno?.toLowerCase() || '';
        const dni = cliente.datos?.dni?.toLowerCase() || '';

        const fullText = `${nombre} ${apellidoPaterno} ${apellidoMaterno}`.toLowerCase();

        return termWords.every(word =>
          fullText.includes(word) || dni.includes(word)
        );
      });
    }

    setFilteredClientes(result);
    setCurrentPage(1);
  };

  const clearAllFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setAvalFilter('all');
  };

  // paginación
  const indexOfLastCliente = currentPage * clientesPerPage;
  const indexOfFirstCliente = indexOfLastCliente - clientesPerPage;
  const currentClientes = filteredClientes.slice(indexOfFirstCliente, indexOfLastCliente);
  const totalPages = Math.ceil(filteredClientes.length / clientesPerPage);

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      pageNumbers.push(1);

      let startPage = Math.max(2, currentPage - Math.floor(maxVisiblePages / 2));
      let endPage = Math.min(totalPages - 1, startPage + maxVisiblePages - 3);

      if (endPage === totalPages - 1) {
        startPage = Math.max(2, endPage - (maxVisiblePages - 3));
      }

      if (startPage > 2) {
        pageNumbers.push('...');
      }

      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }

      if (endPage < totalPages - 1) {
        pageNumbers.push('...');
      }

      pageNumbers.push(totalPages);
    }

    return pageNumbers;
  };

  const handleClientAdded = (newClientResponse) => {
    const newClient = newClientResponse.cliente;
    setClientes([newClient, ...clientes]);
    resetForm();
    fetchClientes(); // refresca para que todo quede ok
  };

  const handleClientUpdated = (updatedClientResponse) => {
    const updatedClient = updatedClientResponse.cliente;
    setClientes(clientes.map(cliente =>
      cliente.idUsuario === updatedClient.idUsuario ? updatedClient : cliente
    ));
    resetForm();
  };

  const handleStatusChange = (clientId, newStatus) => {
    setClientes(clientes.map(cliente =>
      cliente.idUsuario === clientId ? { ...cliente, estado: newStatus } : cliente
    ));
  };

  // EDITAR CON SCROLL AL CONTENEDOR
  const handleEditClient = (cliente) => {
    setIsEditing(true);
    setEditingClient(cliente);
    setShowForm(true);
    scrollToTopMainContainer(); // Solo este scroll
  };

  const resetForm = () => {
    setIsEditing(false);
    setEditingClient(null);
    setShowForm(false);
  };

  const toggleForm = () => {
    if (isEditing) {
      resetForm();
    } else {
      setShowForm(!showForm);
      if (!showForm) {
        scrollToTopMainContainer();
      }
    }
  };

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const labelClass = 'mb-1 block text-xs font-semibold uppercase tracking-[0.12em] text-slate-600';
  const inputClass = 'w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 transition focus:border-red-400 focus:outline-none focus:ring-2 focus:ring-red-200';
  const selectClass = inputClass;
  const paginationButtonClass = 'inline-flex items-center rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50';

  return (
    <div id="main-scroll-container" className="space-y-6 p-4">
      <section className="ui-card relative overflow-hidden px-6 py-7 md:px-8">
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_88%_10%,rgba(217,44,52,0.12),transparent_38%)]" />
            <div className="relative">
              <p className="text-xs font-semibold uppercase tracking-[0.24em] text-red-600">Gestión comercial</p>
              <h1 className="mt-2 text-2xl font-semibold text-slate-900 md:text-3xl">Registro de clientes</h1>
              <p className="mt-2 text-sm text-slate-600">
                Administra altas, edición y estado de clientes con filtros rápidos por perfil y disponibilidad.
              </p>
            </div>
      </section>

      <section className="ui-card p-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <h2 className="text-lg font-semibold text-slate-900">{isEditing ? 'Edición de cliente' : 'Operaciones de cliente'}</h2>
              <button
                onClick={toggleForm}
                className={`${isEditing || showForm ? 'bg-slate-600 hover:bg-slate-700' : 'bg-red-600 hover:bg-red-700'} inline-flex w-full items-center justify-center rounded-xl px-4 py-2 text-sm font-semibold text-white transition sm:w-auto`}
              >
                {isEditing ? (
                  'CANCELAR EDICIÓN'
                ) : showForm ? (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" className="mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    CERRAR FORMULARIO
                  </>
                ) : (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" className="mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    AGREGAR NUEVO CLIENTE
                  </>
                )}
              </button>
            </div>
      </section>

      {showForm && (
        <section className="ui-card overflow-hidden">
              <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
                <h3 className="text-base font-semibold text-slate-900">{isEditing ? 'EDITAR CLIENTE' : 'AGREGAR NUEVO CLIENTE'}</h3>
                <button
                  onClick={resetForm}
                  className="text-slate-500 transition hover:text-slate-700"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="p-4">
                <FormClientes
                  onClientAdded={handleClientAdded}
                  onClientUpdated={handleClientUpdated}
                  initialData={editingClient}
                  isEditing={isEditing}
                  onCancel={resetForm}
                  apiBasePath={apiBasePath}
                />
              </div>
        </section>
      )}

      <section className="ui-card p-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label htmlFor="search" className={labelClass}>
                  BUSCAR CLIENTE
                </label>
                <div className="relative">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <svg className="h-5 w-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    id="search"
                    placeholder="BUSCAR POR NOMBRES, APELLIDOS O DNI..."
                    className={`pl-10 ${inputClass}`}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="status" className={labelClass}>
                  FILTRAR POR ESTADO
                </label>
                <select
                  id="status"
                  className={selectClass}
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">TODOS LOS ESTADOS</option>
                  <option value="1">ACTIVOS</option>
                  <option value="0">INACTIVOS</option>
                </select>
              </div>

              <div>
                <label htmlFor="aval" className={labelClass}>
                  FILTRAR POR AVAL
                </label>
                <select
                  id="aval"
                  className={selectClass}
                  value={avalFilter}
                  onChange={(e) => setAvalFilter(e.target.value)}
                >
                  <option value="all">TODOS LOS CLIENTES</option>
                  <option value="true">SOLO AVALES</option>
                  <option value="false">NO AVALES</option>
                </select>
              </div>

              <div className="flex items-end">
                <button
                  onClick={clearAllFilters}
                  className="inline-flex w-full items-center justify-center rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  LIMPIAR FILTROS
                </button>
              </div>
            </div>
      </section>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700">
          {error}
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
          {Array(6).fill().map((_, index) => (
            <SkeletonClienteCard key={`skeleton-${index}`} />
          ))}
        </div>
      ) : (
        <section className="ui-card p-4">
              <div className="mb-3 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                <h2 className="text-lg font-semibold text-slate-900">LISTADO DE CLIENTES</h2>
                <div className="text-sm text-slate-600">
                  {filteredClientes.length > 0 ? (
                    `MOSTRANDO ${indexOfFirstCliente + 1} - ${Math.min(indexOfLastCliente, filteredClientes.length)} de ${filteredClientes.length}`
                  ) : (
                    'NO HAY CLIENTES QUE COINCIDAN CON FILTROS APLICADOS'
                  )}
                </div>
              </div>

              {filteredClientes.length > 0 ? (
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
                  {currentClientes.map((cliente) => (
                    <ClientesCard
                      key={cliente.idUsuario}
                      cliente={cliente}
                      onStatusChange={handleStatusChange}
                      onEdit={() => handleEditClient(cliente)}
                      apiBasePath={apiBasePath}
                    />
                  ))}
                </div>
              ) : (
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-center">
                  <p className="text-slate-600">
                    {clientes.length === 0
                      ? 'NO HAY CLIENTES REGISTRADOS'
                      : 'NO SE ENCONTRARON CLIENTES QUE COINCIDAN CON LOS CRITERIOS DE BÚSQUEDA'}
                  </p>
                </div>
              )}

              {totalPages > 1 && (
                <div className="mb-2 mt-5 flex justify-center">
                  <nav className="flex items-center space-x-1" aria-label="Pagination">
                    <button
                      onClick={() => paginate(currentPage > 1 ? currentPage - 1 : 1)}
                      disabled={currentPage === 1}
                      className={paginationButtonClass}
                    >
                      <span className="sr-only">ANTERIOR</span>
                      <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </button>

                    {getPageNumbers().map((pageNumber, index) =>
                      pageNumber === '...' ? (
                        <span key={`ellipsis-${index}`} className="inline-flex items-center rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700">
                          ...
                        </span>
                      ) : (
                        <button
                          key={`page-${pageNumber}`}
                          onClick={() => typeof pageNumber === 'number' && paginate(pageNumber)}
                          className={`inline-flex items-center rounded-lg border px-3 py-2 text-sm font-medium transition ${currentPage === pageNumber
                            ? 'border-red-600 bg-red-600 text-white'
                            : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                            }`}
                          aria-current={currentPage === pageNumber ? 'page' : undefined}
                        >
                          {pageNumber}
                        </button>
                      )
                    )}

                    <button
                      onClick={() => paginate(currentPage < totalPages ? currentPage + 1 : totalPages)}
                      disabled={currentPage === totalPages}
                      className={paginationButtonClass}
                    >
                      <span className="sr-only">SIGUIENTE</span>
                      <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </nav>
                </div>
              )}
        </section>
      )}
    </div>
  );
};

export default ClientesUI;
